// ─────────────────────────────────────────────────────────────────
// Glass-i — pivotUtils (Stage 6A Grid Increment 3)
//
// Pure utility functions for building an in-memory pivot table from
// an array of rows. Intended for analytical PREVIEW use only — the
// caller passes in the rows they already have in the browser (the
// current grid page or a small dataset). No network calls. No DOM.
//
// ⚠ PERFORMANCE CONTRACT ⚠
// This pivot is appropriate for:
//   - the current grid page (<= a few hundred rows), or
//   - a small dataset the analyst has already loaded.
//
// It is NOT a BI engine. For dashboards that aggregate over millions
// of rows you MUST do the work in PostgreSQL (GROUP BY, materialised
// views, or a separate reporting schema). See the developer guide
// §17 for the long-form policy.
//
// ─────────────────────────────────────────────────────────────────
//
// CONFIG SHAPE
//
//   {
//     rows:    ['billing_state'],          // 0..N row-axis dataIndex
//     columns: ['status'],                 // 0..N column-axis dataIndex
//     values:  [
//       { field: 'credit_limit', aggregate: 'sum', label?: 'Total Credit Limit' }
//     ],
//     filters: GridAdvancedFilterGroup | null   // optional pre-filter
//   }
//
//   `aggregate` ∈ {'sum','count','avg','min','max'}. Anything else
//   is treated as `count`.
//
//   `rows` and `columns` arrays are walked in order, producing
//   composite group keys joined by ` / `. Multi-field axes are
//   supported but kept lightweight — the UI in ErpPivotView only
//   exposes single-field axes today; the multi-field code path here
//   is forward-compatible.
//
// OUTPUT SHAPE (from buildPivotMatrix)
//
//   {
//     rowKeys:    [{ key, label, parts }],
//     columnKeys: [{ key, label, parts }],
//     matrix:     Map<rowKey, Map<colKey, valueByMeasure>>,
//     totals: {
//       row:    Map<rowKey, valueByMeasure>,
//       column: Map<colKey, valueByMeasure>,
//       grand:  valueByMeasure
//     }
//   }
//
//   `valueByMeasure` is `Map<measureKey, number|null>`. Pivot cells
//   that have no contributing rows hold `null` so the renderer can
//   draw an em-dash.
//
// ─────────────────────────────────────────────────────────────────

'use strict';

/** @typedef {import('./gridTypes').GridAdvancedFilterGroup} GridAdvancedFilterGroup */

/**
 * @typedef {Object} PivotMeasure
 * @property {string} field
 * @property {'sum'|'count'|'avg'|'min'|'max'} aggregate
 * @property {string} [label]
 */

/**
 * @typedef {Object} PivotConfig
 * @property {string[]} rows
 * @property {string[]} columns
 * @property {PivotMeasure[]} values
 * @property {GridAdvancedFilterGroup|null} [filters]
 */

// Resolve a (possibly dotted) dataIndex into a value. Mirrors the
// grid's own readPath so behaviour stays consistent between the
// pivot and the source grid. Pure.
function readPath(record, path) {
  if (!record || path == null) return undefined;
  if (Array.isArray(path)) {
    let cur = record;
    for (const k of path) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }
  if (typeof path === 'string' && path.includes('.')) {
    return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), record);
  }
  return record[path];
}

// Normalise a primitive value into a stable string key. Null /
// undefined / '' all collapse to '(none)' so they share one bucket.
function keyOf(value) {
  if (value === null || value === undefined || value === '') return '(none)';
  return String(value);
}

// Compose a multi-field axis label from its parts. The renderer can
// also display each part separately via the returned `parts` array.
function joinAxisLabel(parts) {
  if (!Array.isArray(parts) || parts.length === 0) return '(none)';
  if (parts.length === 1) return parts[0];
  return parts.join(' / ');
}

/**
 * Aggregate a column of values down to a single number. Returns null
 * when the kind doesn't apply (e.g. `sum` over no numeric values).
 * Identical contract to `aggregateRowsForColumn` in gridQuery.js so
 * the column-aggregate footer and pivot agree on math.
 *
 * @param {'sum'|'count'|'avg'|'min'|'max'} kind
 * @param {any[]} values
 */
export function aggregatePivotValue(kind, values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  if (kind === 'count') return values.length;
  const nums = values.filter(
    (v) => typeof v === 'number' && Number.isFinite(v)
  );
  if (nums.length === 0) return null;
  switch (kind) {
    case 'sum':
      return nums.reduce((a, b) => a + b, 0);
    case 'avg':
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    default:
      // Unknown kinds collapse to count so the matrix at least
      // reports row counts instead of throwing.
      return values.length;
  }
}

/**
 * Build a sorted, de-duplicated axis-key list for a single axis.
 * Each entry carries the composite string key, a human label, and
 * the raw `parts` array (useful for multi-level row/column headers).
 *
 * Sort: ascending on the composite key. Mixed-type axes use string
 * comparison since the keys are already string-coerced.
 *
 * @param {any[]} rows
 * @param {string[]} fields  — dataIndex names; may be empty
 * @returns {Array<{key:string,label:string,parts:string[]}>}
 */
function buildAxisKeys(rows, fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    // Single "(all)" bucket so the matrix is 1 × N or N × 1 instead
    // of degenerate. Lets the caller render a single header column.
    return [{ key: '__all__', label: 'Total', parts: ['Total'] }];
  }
  const seen = new Map();
  for (const r of rows || []) {
    const parts = fields.map((f) => keyOf(readPath(r, f)));
    const compositeKey = parts.join('␟'); // unit-separator — no collision with user data
    if (!seen.has(compositeKey)) {
      seen.set(compositeKey, { key: compositeKey, label: joinAxisLabel(parts), parts });
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Compute the composite axis key for a single row. Mirrors the key
 * shape produced by buildAxisKeys so the same row reliably lands in
 * the matrix cell its axis-key entry described.
 */
function rowAxisKey(row, fields) {
  if (!Array.isArray(fields) || fields.length === 0) return '__all__';
  const parts = fields.map((f) => keyOf(readPath(row, f)));
  return parts.join('␟');
}

/**
 * Build a pivot matrix from in-memory rows + a pivot config. Pure.
 *
 * @param {any[]} rows
 * @param {PivotConfig} config
 * @returns {{
 *   rowKeys:    Array<{key:string,label:string,parts:string[]}>,
 *   columnKeys: Array<{key:string,label:string,parts:string[]}>,
 *   matrix:     Map<string, Map<string, Map<string, number|null>>>,
 *   totals: {
 *     row:    Map<string, Map<string, number|null>>,
 *     column: Map<string, Map<string, number|null>>,
 *     grand:  Map<string, number|null>
 *   },
 *   measures: PivotMeasure[]
 * }}
 */
export function buildPivotMatrix(rows, config) {
  const safeConfig = config && typeof config === 'object' ? config : {};
  const rowFields = Array.isArray(safeConfig.rows) ? safeConfig.rows : [];
  const colFields = Array.isArray(safeConfig.columns) ? safeConfig.columns : [];
  const measures = Array.isArray(safeConfig.values) ? safeConfig.values : [];

  const data = Array.isArray(rows) ? rows : [];

  const rowKeys = buildAxisKeys(data, rowFields);
  const columnKeys = buildAxisKeys(data, colFields);

  // Bucket rows by (rowKey, colKey) → contributions per measure.
  const buckets = new Map();
  const ensureBucket = (rk, ck) => {
    let row = buckets.get(rk);
    if (!row) {
      row = new Map();
      buckets.set(rk, row);
    }
    let cell = row.get(ck);
    if (!cell) {
      cell = new Map();
      measures.forEach((m) => cell.set(measureKeyOf(m), []));
      row.set(ck, cell);
    }
    return cell;
  };

  // Per-row totals + per-column totals + grand total all aggregate
  // the same source values, so we accumulate into all four buckets
  // (cell, row-total, column-total, grand-total) in one pass.
  const rowTotalsRaw = new Map();
  const colTotalsRaw = new Map();
  const grandRaw = new Map();
  const ensureSlot = (map, key) => {
    let slot = map.get(key);
    if (!slot) {
      slot = new Map();
      measures.forEach((m) => slot.set(measureKeyOf(m), []));
      map.set(key, slot);
    }
    return slot;
  };

  for (const r of data) {
    const rk = rowAxisKey(r, rowFields);
    const ck = rowAxisKey(r, colFields);
    const cell = ensureBucket(rk, ck);
    const rowTotalSlot = ensureSlot(rowTotalsRaw, rk);
    const colTotalSlot = ensureSlot(colTotalsRaw, ck);
    const grandSlot = grandRaw.size === 0 ? null : grandRaw;
    if (!grandSlot) {
      measures.forEach((m) => grandRaw.set(measureKeyOf(m), []));
    }
    for (const m of measures) {
      const val = readPath(r, m.field);
      const mk = measureKeyOf(m);
      cell.get(mk).push(val);
      rowTotalSlot.get(mk).push(val);
      colTotalSlot.get(mk).push(val);
      grandRaw.get(mk).push(val);
    }
  }

  // Reduce raw-value lists down to per-measure aggregates.
  const reduceSlot = (slot) => {
    const out = new Map();
    for (const m of measures) {
      const mk = measureKeyOf(m);
      out.set(mk, aggregatePivotValue(m.aggregate || 'count', slot.get(mk) || []));
    }
    return out;
  };

  const matrix = new Map();
  for (const rk of rowKeys) {
    const inner = new Map();
    const rowBucket = buckets.get(rk.key);
    for (const ck of columnKeys) {
      if (rowBucket && rowBucket.has(ck.key)) {
        inner.set(ck.key, reduceSlot(rowBucket.get(ck.key)));
      } else {
        // No source row landed in this cell — emit explicit nulls so
        // the renderer can draw a muted em-dash.
        const empty = new Map();
        measures.forEach((m) => empty.set(measureKeyOf(m), null));
        inner.set(ck.key, empty);
      }
    }
    matrix.set(rk.key, inner);
  }

  const rowTotals = new Map();
  for (const rk of rowKeys) {
    rowTotals.set(rk.key, rowTotalsRaw.has(rk.key) ? reduceSlot(rowTotalsRaw.get(rk.key)) : emptyMeasureMap(measures));
  }
  const colTotals = new Map();
  for (const ck of columnKeys) {
    colTotals.set(ck.key, colTotalsRaw.has(ck.key) ? reduceSlot(colTotalsRaw.get(ck.key)) : emptyMeasureMap(measures));
  }
  const grand = grandRaw.size === 0 ? emptyMeasureMap(measures) : reduceSlot(grandRaw);

  return {
    rowKeys,
    columnKeys,
    matrix,
    totals: { row: rowTotals, column: colTotals, grand },
    measures
  };
}

function emptyMeasureMap(measures) {
  const out = new Map();
  for (const m of measures || []) out.set(measureKeyOf(m), null);
  return out;
}

/**
 * Stable string identifier for a measure. Includes both field and
 * aggregate so the same field can appear twice in `values` (e.g.
 * once as sum, once as avg).
 *
 * @param {PivotMeasure} m
 */
export function measureKeyOf(m) {
  if (!m) return 'count:*';
  return `${m.aggregate || 'count'}:${m.field || '*'}`;
}

/**
 * Format a pivot cell value for display. Numbers use Indian numbering
 * (matching the rest of the grid). Null becomes the muted em-dash
 * (passed back as a string so the caller can swap it for a JSX node).
 *
 * @param {number|null|undefined} value
 * @param {{ kind?:string, fractionDigits?:number }} [opts]
 */
export function formatPivotCell(value, opts) {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'number') return String(value);
  if (!Number.isFinite(value)) return '—';
  const o = opts || {};
  if (Number.isInteger(value)) return value.toLocaleString('en-IN');
  const fd = Number.isFinite(o.fractionDigits) ? o.fractionDigits : 2;
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: fd,
    maximumFractionDigits: fd
  });
}

/**
 * Convenience: return the row-axis keys produced by `buildPivotMatrix`.
 * Exposed separately so a caller can pre-flight a config (e.g. UI
 * preview of "this pivot will have 27 rows") without doing the
 * full aggregate pass.
 */
export function getPivotRowKeys(rows, rowFields) {
  return buildAxisKeys(Array.isArray(rows) ? rows : [], Array.isArray(rowFields) ? rowFields : []);
}

/**
 * Convenience: return the column-axis keys. Symmetric to
 * `getPivotRowKeys`.
 */
export function getPivotColumnKeys(rows, columnFields) {
  return buildAxisKeys(Array.isArray(rows) ? rows : [], Array.isArray(columnFields) ? columnFields : []);
}

// CommonJS / ESM dual-friendly re-export. The file is consumed both
// from the browser (Vite/ESM) and from Node's `node:test` (which
// loads `.js` as ESM because frontend/package.json has `type:module`).
// No CommonJS shim needed — but kept in one file so we don't fragment
// pivot logic across multiple modules.
